import {EnvironmentInjector, Injectable, resource, Resource, runInInjectionContext} from '@angular/core';
import {DatasetResponse, OrganizationResponse as LSOrganizationResponse, ProjectResponse} from '@backend';
import {
  OrganizationResponse as MWOrganizationResponse,
  OrganizationService,
  PublicDatasetResponse,
  UserResponse,
} from '@middleware';
import {CommonsSearchDataset, CommonsSearchProject} from '@models/commons-types';
import {lastValueFrom} from 'rxjs';
import {OrgServiceInstanceMap, ServiceClasses, ServiceInstanceMap} from './service.map';

export type RDEObjectString =
  | 'string'
  | 'MWOrganizationResponse'
  | 'LSOrganizationResponse'
  | 'UserResponse'
  | 'ProjectResponse'
  | 'DatasetResponse'
  | 'PublicDatasetResponse';
export type RDEObject =
  | string
  | MWOrganizationResponse
  | LSOrganizationResponse
  | UserResponse
  | ProjectResponse
  | DatasetResponse
  | PublicDatasetResponse
  | CommonsSearchDataset
  | CommonsSearchProject;

@Injectable({
  providedIn: 'root',
})
export class LandingServiceBrokerService {
  constructor(
    public readonly orgService: OrganizationService,
    private readonly injector: EnvironmentInjector,
  ) {}

  services: OrgServiceInstanceMap = {};
  resources: Record<string, Resource<ServiceInstanceMap>> = {};

  // If an object takes an abnormal form, you can also get the ServiceInstanceMap by org name directly:
  async getByOrganizationName(orgName: string): Promise<ServiceInstanceMap | undefined> {
    if (orgName == undefined) return undefined;
    if (this.services[orgName]) return this.services[orgName];

    const orgs = await lastValueFrom(this.orgService.getOrganizations());
    const org = orgs.organizations.find(o => o.name === orgName);

    if (!org?.data_enclave_url) return undefined;

    this.services[orgName] = this.build(org.data_enclave_url);
    return this.services[orgName];
  }

  getResource(orgName: string | (() => string)): Resource<ServiceInstanceMap> {
    if (typeof orgName === 'function') {
      return resource({
        params: () => ({orgName: orgName()}),
        loader: ({params}) => this.getByOrganizationName(params.orgName),
      });
    }
    return (this.resources[orgName] ??= resource({loader: () => this.getByOrganizationName(orgName)}));
  }

  build(url: string): ServiceInstanceMap {
    return Object.fromEntries(
      Object.entries(ServiceClasses).map(([name, cls]) => {
        return runInInjectionContext(this.injector, () => {
          const serviceInstance = new cls();
          serviceInstance.baseUrl = url;
          return [name, serviceInstance];
        });
      }),
    ) as unknown as ServiceInstanceMap;
  }
}
